/**
 * PendingSignatures — what literally needs my signature today.
 *
 * Aggregates pending metric snapshots, CAPA effectiveness checks awaiting
 * the QA Director, and audit findings still in PENDING disposition.
 */

import { Link } from '@tanstack/react-router';
import { usePendingSignatures, type PendingSignature } from '../../api/orgHooks';
import styles from './HomeDashboardCards.module.css';

const KIND_ICON: Record<string, string> = {
  METRIC_SNAPSHOT: '📊',
  CAPA_EFFECTIVENESS: '✅',
  AUDIT_FINDING_DISPOSITION: '🔍',
};

const KIND_LABEL: Record<string, string> = {
  METRIC_SNAPSHOT: 'Snapshot',
  CAPA_EFFECTIVENESS: 'CAPA Eff.',
  AUDIT_FINDING_DISPOSITION: 'Audit',
};

export function PendingSignatures() {
  const q = usePendingSignatures();
  const items = q.data?.items ?? [];
  if (!q.isLoading && items.length === 0) {
    return (
      <section className={styles.signaturesCard} aria-label="Pending signatures">
        <header className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Awaiting your signature</h2>
          <span className={styles.cardCount}>0</span>
        </header>
        <p className={styles.empty}>Inbox clear — nothing waiting on you right now.</p>
      </section>
    );
  }

  return (
    <section className={styles.signaturesCard} aria-label="Pending signatures">
      <header className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Awaiting your signature</h2>
        <span className={styles.cardCount}>{items.length}</span>
      </header>

      <ul className={styles.signaturesList}>
        {items.slice(0, 6).map((s: PendingSignature) => (
          <li key={`${s.kind}-${s.id}`} className={styles.signatureRow}>
            <Link to={s.link} className={styles.signatureLink}>
              <span className={styles.signatureIcon} aria-hidden>
                {KIND_ICON[s.kind] ?? '📝'}
              </span>
              <span className={styles.signatureBody}>
                <span className={styles.signatureLabel}>{s.label}</span>
                <span className={styles.signatureHint}>{s.hint}</span>
              </span>
              <span
                className={`${styles.urgencyPill} ${styles[`urgency_${s.urgency}`]}`}
                aria-label={`Urgency ${s.urgency}`}
              >
                {KIND_LABEL[s.kind] ?? s.kind}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {items.length > 6 && (
        <footer className={styles.cardFoot}>
          +{items.length - 6} more
        </footer>
      )}
    </section>
  );
}
